// Diagnostics, as plain data and plain text: what the sheet shows and what "Copy report" puts on the clipboard. Pure (no DOM).

export interface Residual {
  /** Song seconds of the tap. */
  readonly t: number;
  /** Milliseconds from the nearest beat of the grid the tracker chose (positive: late). */
  readonly ms: number;
}

export interface ResidualStats {
  readonly n: number;
  /** Signed mean, ms: a steady lag or lead. */
  readonly mean: number;
  /** Mean of the absolute values, ms: how scattered. */
  readonly meanAbs: number;
  readonly worst: number;
}

export function residualStats(r: readonly Residual[]): ResidualStats | null {
  if (r.length === 0) return null;
  const n = r.length;
  const mean = r.reduce((a, x) => a + x.ms, 0) / n;
  const meanAbs = r.reduce((a, x) => a + Math.abs(x.ms), 0) / n;
  const worst = r.reduce((a, x) => (Math.abs(x.ms) > Math.abs(a) ? x.ms : a), 0);
  return { n, mean, meanAbs, worst };
}

export interface TempoSummary {
  readonly min: number;
  readonly max: number;
  readonly first: number;
  readonly last: number;
  readonly points: number;
}

export function tempoSummary(curve: { readonly times: readonly number[]; readonly bpm: readonly number[] }): TempoSummary | null {
  if (curve.bpm.length === 0) return null;
  return { min: Math.min(...curve.bpm), max: Math.max(...curve.bpm), first: curve.bpm[0]!, last: curve.bpm[curve.bpm.length - 1]!, points: curve.bpm.length };
}

/** Bytes in words: 4.2 MB. */
export function sizeWords(n: number): string {
  return n >= 1e9 ? `${(n / 1e9).toFixed(1)} GB` : n >= 1e6 ? `${(n / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1e3))} kB`;
}

/** Signed milliseconds with one decimal: "+12.3 ms", "-4.0 ms". */
export const signedMs = (ms: number): string => `${ms >= 0 ? '+' : '-'}${Math.abs(ms).toFixed(1)} ms`;
export const plainMs = (s: number): string => `${(s * 1000).toFixed(1)} ms`;

export interface ReportInput {
  readonly generatedAt: string;
  readonly track: { readonly name: string; readonly id: string; readonly duration: number } | null;
  readonly source: 'none' | 'file' | 'lab';
  readonly status: string;
  readonly tempoBpm: number | null;
  readonly tempoHereBpm: number | null;
  readonly taps: number;
  readonly events: number;
  readonly halfBeat: { readonly shifted: boolean; readonly margin: number; readonly taps: number; readonly unsure: boolean; readonly source?: 'audio' | 'taps'; readonly audioMargin?: number | null } | null;
  readonly clave: { readonly setting: string; readonly evidence: { readonly direction: string; readonly margin: number; readonly percentile: number; readonly phrases: number } | null };
  readonly residuals: readonly Residual[];
  readonly tempoCurve: { readonly times: readonly number[]; readonly bpm: readonly number[] };
  readonly outputLatencyS: number;
  readonly stretcherS: number;
  readonly calibrationOffsetS: number | null;
  readonly calibrationSaved: boolean;
  readonly trackerVersion: string;
  readonly storage: string;
  readonly songsKept: string;
  readonly userAgent: string;
  /** The automatic count (S9e), when the report is made with a song open. */
  readonly auto?: AutoReport;
}

/** The half-beat decision in a sentence. */
export function halfBeatText(h: ReportInput['halfBeat']): string {
  if (!h) return 'not decided yet';
  const move = h.shifted ? 'moved half a beat later' : 'kept the grid as tracked';
  const unsure = h.unsure ? ' (unsure: ½ beat is offered)' : '';
  if (h.source === 'audio' && h.audioMargin != null) return `${move}, decided by the audio (margin ${h.audioMargin.toFixed(1)} nats); the taps' own timing margin is ${h.margin.toFixed(2)} beat from ${h.taps} ${h.taps === 1 ? 'tap' : 'taps'}${unsure}`;
  if (h.audioMargin != null) return `${move}, decided by the taps because the audio was not sure (margin ${h.audioMargin.toFixed(2)} nats); margin ${h.margin.toFixed(2)} beat from ${h.taps} ${h.taps === 1 ? 'tap' : 'taps'}${unsure}`;
  return `${move}; margin ${h.margin.toFixed(2)} beat from ${h.taps} ${h.taps === 1 ? 'tap' : 'taps'}${unsure}`;
}

export function claveText(c: ReportInput['clave']): string {
  const e = c.evidence;
  return `${c.setting}${e ? `; evidence ${e.direction}, margin ${e.margin.toFixed(3)}, ${(e.percentile * 100).toFixed(1)}th percentile over ${e.phrases} phrases` : '; no evidence yet'}`;
}

/** What Diagnostics shows of the automatic count (S9e): the proposal in force, or why there is none. All the words a dancer never sees are here: margins, levels, evidence. */
export interface AutoReport {
  readonly run: string;
  readonly declined: string | null;
  readonly origin: string;
  readonly level: number;
  readonly ms: number | null;
  readonly proposal: {
    readonly bpm: number;
    readonly anchor: number;
    readonly one: number;
    readonly words: Readonly<Record<'tempo' | 'beat' | 'odd' | 'bar' | 'one', string>>;
    readonly margins: Readonly<Record<'tempo' | 'beat' | 'odd' | 'bar' | 'one', number>>;
    readonly evidence: { readonly chosen: number; readonly other: number; readonly events: number; readonly boundaries: number; readonly p: number };
    readonly levels: readonly { readonly bpm: number; readonly score: number }[];
    readonly direction: string;
  } | null;
}

/** The automatic count as label and value rows: the state, the tempo level candidates, every decision's margin and word, which evidence decided "1", the clave and the time it took. */
export function autoRows(a: AutoReport): [string, string][] {
  const runWords: Record<string, string> = { idle: 'not asked (the song already had taps, or it is the Rhythm Lab)', off: 'off (the setting is off)', finding: 'finding…', declined: `declined: ${a.declined ?? 'no reason given'}`, proposed: 'proposed' };
  const rows: [string, string][] = [['Automatic count', runWords[a.run] ?? a.run]];
  const p = a.proposal;
  if (a.run === 'declined' || !p) {
    if (a.ms !== null) rows.push(['Time to propose', `${a.ms.toFixed(0)} ms`]);
    return rows;
  }
  const d = (k: 'tempo' | 'beat' | 'odd' | 'bar' | 'one') => `${p.margins[k].toFixed(2)} nats, ${p.words[k]}`;
  rows.push(
    ['Count comes from', a.origin === 'auto' ? 'the automatic proposal' : a.origin === 'overridden' ? 'the proposal, then a tap of yours' : a.origin === 'taps' ? 'your taps' : 'nothing'],
    ['Tempo level candidates', p.levels.map((l, i) => `${l.bpm.toFixed(1)} BPM (score ${l.score.toFixed(2)})${i === 0 ? ' chosen' : ''}`).join(' · ')],
    ['Level in force', a.level === 0 ? 'as proposed' : a.level < 0 ? 'halved (Too fast)' : 'doubled (Too slow)'],
    ['Margins', `tempo level ${d('tempo')} · beats against the "&"s ${d('beat')} · odd counts ${d('odd')} · bar ${d('bar')} · 1 against 5 ${d('one')}`],
    ['"1" decided by', `phrase evidence: of ${p.evidence.events} strongest spectral changes at bar starts (${p.evidence.boundaries} boundaries measured), ${p.evidence.chosen} fall on the proposed 1 and ${p.evidence.other} on the other candidate, p = ${p.evidence.p.toFixed(3)}; the pattern of the bass and percussion decided the bar, not which of 1 and 5`],
    ['Proposed 1', `${p.one.toFixed(2)} s, the lattice read from ${p.anchor.toFixed(2)} s, tracker centred on ${p.bpm.toFixed(1)} BPM`],
    ['Clave direction named', p.direction === 'none' ? 'none (not significant, or "1" not likely)' : p.direction],
  );
  if (a.ms !== null) rows.push(['Time to propose', `${a.ms.toFixed(0)} ms (the proposal and the track it seeded, in the worker)`]);
  return rows;
}

/** The plain-text report. Every line is "label: value" so it can be pasted into a message and read. */
export function formatReport(r: ReportInput): string {
  const stats = residualStats(r.residuals);
  const tempo = tempoSummary(r.tempoCurve);
  const lines = [
    'salsacount diagnostics',
    `Made: ${r.generatedAt}`,
    `Song: ${r.source === 'lab' ? 'Rhythm Lab' : r.track ? `${r.track.name} (${r.track.duration.toFixed(1)} s, sha256 ${r.track.id.slice(0, 12)}…)` : 'none open'}`,
    `Status: ${r.status}`,
    `Tempo: ${r.tempoBpm ? `${r.tempoBpm.toFixed(1)} BPM mean${r.tempoHereBpm ? `, ${r.tempoHereBpm.toFixed(1)} BPM at the playhead` : ''}` : 'no count yet'}`,
    `Tempo curve: ${tempo ? `${tempo.points} points, ${tempo.min.toFixed(1)} to ${tempo.max.toFixed(1)} BPM, from ${tempo.first.toFixed(1)} to ${tempo.last.toFixed(1)}` : 'not available (needs two taps and the tracker)'}`,
    `Taps in force: ${r.taps}; events in the log: ${r.events}`,
    `Tap residuals: ${stats ? `${stats.n} taps, mean ${signedMs(stats.mean)}, mean absolute ${stats.meanAbs.toFixed(1)} ms, worst ${signedMs(stats.worst)}` : 'not available'}`,
  ];
  if (stats) lines.push(`Residuals by tap (ms): ${r.residuals.map((x) => x.ms.toFixed(1)).join(', ')}`);
  lines.push(
    `Half-beat decision: ${halfBeatText(r.halfBeat)}`,
    ...(r.auto ? autoRows(r.auto).map(([k, v]) => `${k}: ${v}`) : []),
    `Clave: ${claveText(r.clave)}`,
    `Output latency: ${plainMs(r.outputLatencyS)}`,
    `Stretcher residual: ${plainMs(r.stretcherS)}`,
    `Tap offset (calibration): ${r.calibrationOffsetS === null ? 'not calibrated' : `${plainMs(r.calibrationOffsetS)}${r.calibrationSaved ? '' : ' (memory only)'}`}`,
    `Tracker: ${r.trackerVersion}`,
    `Storage used: ${r.storage}`,
    `Songs kept on this device: ${r.songsKept}`,
    `Browser: ${r.userAgent}`,
  );
  return lines.join('\n');
}
